import { useCallback, useMemo, useState } from 'react';

import { findIdByName, normalizeSelectValue } from './selectAssignmentUtils';
import { useAssignmentLookup } from './useAssignmentLookup';

import { DepartmentApi, IDepartmentResponse, IUnitResponse, UnitApi } from 'services';

interface IDepartmentUnitInitialValues {
  departmentId?: number;
  departmentName?: string;
  unitId?: number;
  unitName?: string;
}

interface IUseDepartmentUnitAssignmentProps {
  defaultValue: string;
  organizationId?: number;
  requestScope?: string;
  initialValues?: IDepartmentUnitInitialValues;
}

interface IUseDepartmentUnitAssignmentResult {
  departments: IDepartmentResponse[];
  units: IUnitResponse[];
  departmentValue: string;
  unitValue: string;
  optionsDepartment: string[];
  optionsUnit: string[];
  selectedDepartmentId?: number;
  selectedUnitId?: number;
  isDepartmentsLoading: boolean;
  isUnitsLoading: boolean;
  isAssignmentReady: boolean;
  hasDepartmentsError: boolean;
  hasUnitsError: boolean;
  retryDepartments: () => void;
  retryUnits: () => void;
  onSelectChangeDepartment: (value: string) => void;
  onSelectChangeUnit: (value: string) => void;
}

const EMPTY_DEPARTMENTS: IDepartmentResponse[] = [];
const EMPTY_UNITS: IUnitResponse[] = [];

const useDepartmentUnitAssignment = ({
  defaultValue,
  organizationId,
  requestScope,
  initialValues,
}: IUseDepartmentUnitAssignmentProps): IUseDepartmentUnitAssignmentResult => {
  const initialDepartmentValue = normalizeSelectValue(
    initialValues?.departmentName,
    defaultValue,
  );
  const initialUnitValue = normalizeSelectValue(initialValues?.unitName, defaultValue);

  const [departmentValue, setDepartmentValue] = useState(initialDepartmentValue);
  const [unitValue, setUnitValue] = useState(initialUnitValue);
  const scope = organizationId ? requestScope ?? String(organizationId) : null;
  const readDepartments = useCallback(
    (signal: AbortSignal) => new DepartmentApi().getAllDepartments({ signal }),
    [],
  );
  const departmentLookup = useAssignmentLookup(
    scope,
    readDepartments,
    'Assignment.departments',
  );
  const departments = departmentLookup.data ?? EMPTY_DEPARTMENTS;
  const departmentId =
    findIdByName(departments, departmentValue) ??
    (departmentValue === initialDepartmentValue
      ? initialValues?.departmentId
      : undefined);
  const readUnits = useCallback(
    (signal: AbortSignal) =>
      new UnitApi().getUnitsByDepartment(departmentId!, { signal }),
    [departmentId],
  );
  const unitLookup = useAssignmentLookup(
    scope && departmentId ? `${scope}:${departmentId}` : null,
    readUnits,
    'Assignment.units',
  );
  const units = unitLookup.data ?? EMPTY_UNITS;
  const isDepartmentsLoading = departmentLookup.isLoading;
  const isUnitsLoading = unitLookup.isLoading;

  const selectedDepartmentId =
    findIdByName(departments, departmentValue) ??
    (departmentValue === initialDepartmentValue
      ? initialValues?.departmentId
      : undefined);
  const selectedUnitId =
    findIdByName(units, unitValue) ??
    (unitValue === initialUnitValue ? initialValues?.unitId : undefined);

  const optionsDepartment = useMemo(
    () => departments.map(department => department.name),
    [departments],
  );
  const optionsUnit = useMemo(() => units.map(unit => unit.name), [units]);

  const onSelectChangeDepartment = useCallback(
    (value: string) => {
      setDepartmentValue(value);
      setUnitValue(defaultValue);
    },
    [defaultValue],
  );

  const onSelectChangeUnit = useCallback((value: string) => {
    setUnitValue(value);
  }, []);

  return {
    departments,
    units,
    departmentValue,
    unitValue,
    optionsDepartment,
    optionsUnit,
    selectedDepartmentId,
    selectedUnitId,
    isDepartmentsLoading,
    isUnitsLoading,
    isAssignmentReady: Boolean(scope) && departmentLookup.isReady && unitLookup.isReady,
    hasDepartmentsError: departmentLookup.hasError,
    hasUnitsError: unitLookup.hasError,
    retryDepartments: departmentLookup.retry,
    retryUnits: unitLookup.retry,
    onSelectChangeDepartment,
    onSelectChangeUnit,
  };
};

export { useDepartmentUnitAssignment };
